import { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useGetAllCustomersQuery } from "app/api/customers";
import { Order } from "models/Order";
import { OrderFilters } from "../ordersSlice";
import { useDebounce } from "hooks/useDebounce";

type CustomerSelectProps = {
  value: string | null;
  onChange: (value: Partial<OrderFilters>) => void;
};

const MINIMUM_SEARCH_LENGTH = 3;

const CustomerSelect: React.FC<CustomerSelectProps> = (props) => {
  const { value, onChange } = props;

  const [customerSearch, setCustomerSearch] = useState(value);

  const debouncedCustomerSearch = useDebounce(customerSearch, {
    delayMs: 500,
  });

  const [selectedCustomer, setSelectedCustomer] = useState<Pick<
    Order,
    "companyCode" | "companyName"
  > | null>(null);

  const { data: customers } = useGetAllCustomersQuery(
    { search: debouncedCustomerSearch },
    { skip: !debouncedCustomerSearch || debouncedCustomerSearch.length < MINIMUM_SEARCH_LENGTH }
  );

  const customerOptions = useMemo(() => {
    if (!debouncedCustomerSearch || debouncedCustomerSearch?.length < MINIMUM_SEARCH_LENGTH)
      return [];
    return customers?.filter((c) => c.companyCode) || [];
  }, [customers, debouncedCustomerSearch]);

  useEffect(() => {
    if (value && customerOptions.length > 0) {
      const matchedCustomer = customerOptions.find((c) => c.companyCode === value);
      setSelectedCustomer(matchedCustomer || null);
    } else {
      setSelectedCustomer(null);
    }
  }, [value, customerOptions]);

  return (
    <Autocomplete
      value={selectedCustomer}
      options={customerOptions}
      noOptionsText="Inga resultat"
      getOptionLabel={(option) => `${option.companyName} (${option.companyCode})` || ""}
      onChange={(e, value) => {
        setSelectedCustomer(value);
        onChange({ filterByCompanyCode: value?.companyCode || null });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Kund"
          variant="filled"
          onChange={(e) => setCustomerSearch(e.target.value)}
        />
      )}
    />
  );
};

export default CustomerSelect;
